import { captureException } from '@sentry/nuxt';
import type {
  GetNavigationSlugsQuery,
  GetNavigationSlugsQueryVariables,
} from '~/apollo/types/types';
import { GetNavigationSlugs } from '~/apollo/types/types';

export default async () => {
  const { locale } = useI18n();

  const { data: navSlugs } = await useAsyncData(
    // Reactive key, not an interpolated string: the key has always been
    // locale-scoped, but as a plain string it was captured once at setup, so a
    // locale switch stored the new locale's slugs under the previous locale's
    // key. As a computed, Nuxt swaps to (and fetches for) the new key itself,
    // which also removes the need for a manual `watch(locale)` — see below.
    computed(() => `gp-navigation-product-slugs-${locale.value}`),
    async () => {
      try {
        const { data: articlesData } = await useLegacyApollo<
          GetNavigationSlugsQuery,
          GetNavigationSlugsQueryVariables
        >(GetNavigationSlugs, {
          locale: locale.value,
          productKeys: ['minecraft', 'teamspeak-3'],
        });

        return {
          minecraftSlug: articlesData.products.find(
            (p) => p.productKey === 'minecraft',
          )?.slug,
          teamspeakSlug: articlesData.products.find(
            (p) => p.productKey === 'teamspeak-3',
          )?.slug,
        };
      } catch (error) {
        captureException(error);
      }

      return {
        minecraftSlug: null,
        teamspeakSlug: null,
      };
    },
    // The sidebar renders both menus unconditionally so the auth swap stays
    // reactive after hydration, so firstlevel/index.vue reaches this composable
    // twice per render under the same key. Nuxt's default `dedupe: 'cancel'`
    // aborts the first call's controller and starts a second — but the abort
    // signal never reaches useLegacyApollo, so the first POST still goes out and
    // its result is thrown away. `defer` reuses the in-flight promise instead:
    // one GetNavigationSlugs per render (TV-1845).
    //
    // This covers the locale switch too, which is why there is no `watch(locale)`
    // here any more. Nuxt's own key watcher executes with `dedupe: opts.dedupe`,
    // i.e. the 'defer' above — so the second instance joins the first instance's
    // in-flight promise instead of firing a second POST. A manual watcher could
    // not do that: being per-instance, it ran twice per switch.
    { dedupe: 'defer' },
  );

  return {
    navSlugs,
  };
};
